import { Typography, Card, Button, Tag, message, List } from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, linkWith } from '../api/client';
import PageHeader from '../components/PageHeader';

const PROVIDERS: { key: 'line' | 'yahoo' | 'google'; label: string; color: string }[] = [
  { key: 'line', label: 'LINE', color: '#06C755' },
  { key: 'yahoo', label: 'Yahoo', color: '#6001D2' },
  { key: 'google', label: 'Google', color: '#4285F4' },
];

/** 我的帳號：一個帳號可綁多種登入方式（SSO）。 */
export default function AccountPage() {
  const qc = useQueryClient();
  const ids = useQuery({ queryKey: ['identities'], queryFn: api.identities });

  // 從 /api/auth/.../link 連結完導回時的提示
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('linked')) message.success('已連結登入方式');
    if (p.get('error')) message.error(decodeURIComponent(p.get('error')!));
    if (p.get('linked') || p.get('error')) window.history.replaceState({}, '', '/account');
  }, []);

  const unlink = useMutation({
    mutationFn: (provider: string) => api.unlinkProvider(provider),
    onSuccess: () => {
      message.success('已解除');
      qc.invalidateQueries({ queryKey: ['identities'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const linked = new Map((ids.data ?? []).map((i) => [i.provider, i.email]));

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="我的帳號" subtitle="管理綁定的登入方式（SSO）" />
      <Card title="登入方式" loading={ids.isLoading}>
        <p className="mb-1 text-xs text-gray-500">可綁定多種，任一種都能登入此帳號。</p>
        <List
          dataSource={PROVIDERS}
          renderItem={(p) => {
            const isLinked = linked.has(p.key);
            const email = linked.get(p.key);
            return (
              <List.Item
                actions={[
                  isLinked ? (
                    <Button
                      key="u"
                      danger
                      size="small"
                      icon={<DisconnectOutlined />}
                      loading={unlink.isPending}
                      disabled={(ids.data?.length ?? 0) <= 1}
                      onClick={() => unlink.mutate(p.key)}
                    >
                      解除
                    </Button>
                  ) : (
                    <Button key="l" type="primary" ghost size="small" icon={<LinkOutlined />} onClick={() => linkWith(p.key)}>
                      連結
                    </Button>
                  ),
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full font-bold text-white"
                      style={{ background: p.color }}
                    >
                      {p.label[0]}
                    </span>
                  }
                  title={p.label}
                  description={
                    isLinked ? (
                      <Tag color="green" className="!me-0 !whitespace-normal break-all">已連結{email ? ` · ${email}` : ''}</Tag>
                    ) : (
                      <span className="text-gray-400">未連結</span>
                    )
                  }
                />
              </List.Item>
            );
          }}
        />
        <Typography.Text type="secondary" className="text-xs">
          至少需保留一種登入方式。
        </Typography.Text>
      </Card>
    </div>
  );
}
